import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Shield,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Phone,
  X,
  Save,
  RefreshCw,
  UserCheck,
  UserX,
  Lock,
  Mail,
  CheckCircle,
} from "lucide-react";

const UserRoles = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");

  // Form States
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    role: "admin", // Default
  });

  // Mock Fetch (Replace with actual API call)
  useEffect(() => {
    // Simulating API fetch
    setLoading(true);
    setTimeout(() => {
      setUsers([
        {
          id: 1,
          firstName: "Admin",
          lastName: "User",
          email: "admin@setu.com",
          role: "Super Admin",
          phone: "9876543210",
          status: "Active",
          lastActive: "2 mins ago",
        },
        {
          id: 2,
          firstName: "John",
          lastName: "Doe",
          email: "john@test.com",
          role: "Editor",
          phone: "9876543211",
          status: "Active",
          lastActive: "1 hour ago",
        },
        {
          id: 3,
          firstName: "Jane",
          lastName: "Smith",
          email: "jane@test.com",
          role: "Viewer",
          phone: "9876543212",
          status: "Inactive",
          lastActive: "2 days ago",
        },
        {
          id: 4,
          firstName: "Robert",
          lastName: "Brown",
          email: "robert@test.com",
          role: "Teacher",
          phone: "9876543213",
          status: "Active",
          lastActive: "5 mins ago",
        },
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newUser = {
      ...formData,
      id: users.length + 1,
      status: "Active",
      lastActive: "Just now",
    };
    setUsers([...users, newUser]);
    setIsModalOpen(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      role: "admin",
    });
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "super admin":
        return "text-purple-400 bg-purple-500/10 border-purple-500/20";
      case "admin":
        return "text-blue-400 bg-blue-500/10 border-blue-500/20";
      case "editor":
        return "text-orange-400 bg-orange-500/10 border-orange-500/20";
      case "teacher":
        return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
      default:
        return "text-gray-400 bg-gray-500/10 border-gray-500/20";
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      activeFilter === "All" || user.status === activeFilter;
    return matchesSearch && matchesFilter;
  });

  // Calculate Stats
  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.status === "Active").length;
  const inactiveUsers = users.filter((u) => u.status === "Inactive").length;

  const StatCard = ({ title, value, icon, color }) => (
    <div className="bg-gray-800 rounded-2xl p-5 border border-gray-700 flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-sm font-medium mb-1">{title}</p>
        <h3 className="text-2xl font-bold text-white">{value}</h3>
      </div>
      <div className={`p-3 rounded-xl ${color} bg-opacity-20`}>{icon}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <motion.div
        className="max-w-7xl mx-auto space-y-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-2xl shadow-lg shadow-indigo-500/20">
              <Users className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-br from-indigo-400 to-violet-400 bg-clip-text text-transparent">
                User Management
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                Manage system access, roles, and permissions
              </p>
            </div>
          </div>

          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-xl hover:shadow-lg hover:shadow-indigo-500/20 transition-all font-semibold transform hover:-translate-y-0.5"
          >
            <Plus className="w-5 h-5" />
            Add New User
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Total Users"
            value={totalUsers}
            icon={<Users className="w-6 h-6 text-blue-400" />}
            color="bg-blue-500"
          />
          <StatCard
            title="Active Users"
            value={activeUsers}
            icon={<UserCheck className="w-6 h-6 text-green-400" />}
            color="bg-green-500"
          />
          <StatCard
            title="Inactive Users"
            value={inactiveUsers}
            icon={<UserX className="w-6 h-6 text-red-400" />}
            color="bg-red-500"
          />
        </div>

        {/* Main Content Card */}
        <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden shadow-xl">
          {/* Toolbar */}
          <div className="p-5 border-b border-gray-700 flex flex-col md:flex-row gap-4 justify-between items-center">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-900 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex bg-gray-900 p-1 rounded-xl border border-gray-700">
              {["All", "Active", "Inactive"].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    activeFilter === filter
                      ? "bg-gray-800 text-white shadow-sm"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-20 flex flex-col items-center justify-center text-gray-500">
                <RefreshCw className="w-10 h-10 animate-spin mb-4 text-indigo-500" />
                <p>Loading users...</p>
              </div>
            ) : (
              <table className="w-full text-left">
                <thead className="bg-gray-900/50 text-gray-400 text-xs uppercase font-semibold">
                  <tr>
                    <th className="px-6 py-4">User</th>
                    <th className="px-6 py-4">Role</th>
                    <th className="px-6 py-4">Contact</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="hover:bg-gray-700/30 transition-colors group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center text-white font-bold border border-gray-600 shadow-inner">
                              {user.firstName[0]}
                              {user.lastName[0]}
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {user.firstName} {user.lastName}
                              </p>
                              <p className="text-gray-500 text-xs">
                                {user.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium border ${getRoleColor(
                              user.role
                            )}`}
                          >
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-400 text-sm">
                          <div className="flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5" /> {user.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span
                              className={`w-2 h-2 rounded-full ${
                                user.status === "Active"
                                  ? "bg-green-500"
                                  : "bg-red-500"
                              }`}
                            ></span>
                            <span
                              className={`text-sm ${
                                user.status === "Active"
                                  ? "text-gray-300"
                                  : "text-gray-500"
                              }`}
                            >
                              {user.status}
                            </span>
                          </div>
                          <p className="text-[10px] text-gray-600 mt-0.5 ml-4">
                            Active {user.lastActive}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              className="p-2 text-indigo-400 hover:bg-indigo-500/10 rounded-lg transition-colors tooltip"
                              title="Edit User"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors tooltip"
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <Search className="w-12 h-12 mb-4 opacity-20" />
                          <p className="text-lg font-medium text-gray-400">
                            No users found
                          </p>
                          <p className="text-sm">
                            Try adjusting your search or filters
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </motion.div>

      {/* Add User Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-gray-800 rounded-2xl w-full max-w-xl border border-gray-700 overflow-hidden shadow-2xl"
            >
              <div className="p-6 border-b border-gray-700 flex justify-between items-center bg-gray-900/50">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    Add New User
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Enter the details to create a new account
                  </p>
                </div>

                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">
                      First Name
                    </label>
                    <input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. John"
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl p-3 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">
                      Last Name
                    </label>
                    <input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. Doe"
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl p-3 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5" /> Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@example.com"
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl p-3 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5" /> Phone Number
                    </label>
                    <input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+91 98765 43210"
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl p-3 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-2">
                      <Shield className="w-3.5 h-3.5" /> Role
                    </label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl p-3 text-white focus:outline-none focus:border-indigo-500 transition-colors appearance-none"
                    >
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="teacher">Teacher</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-2">
                      <Lock className="w-3.5 h-3.5" /> Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      placeholder="••••••••"
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl p-3 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-700 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-5 py-2.5 bg-gray-700 text-gray-300 rounded-xl hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-xl hover:shadow-lg hover:shadow-indigo-500/25 transition-all font-semibold flex items-center gap-2 transform active:scale-95"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Create Account
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserRoles;
